import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from './use-toast';

export interface EmpPenalidade {
  id: string;
  contrato_id: string;
  codigo: string;
  tipo: 'atraso' | 'nao_conformidade' | 'desempenho' | 'seguranca';
  descricao: string;
  clausula_contratual: string | null;
  impacto_descricao: string | null;
  ativo: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface EmpPenalidadeOcorrencia {
  id: string;
  penalidade_id: string;
  contrato_id: string;
  atividade_id: string | null;
  desvio_id: string | null;
  status: 'identificada' | 'notificada' | 'aplicada' | 'contestada' | 'cancelada';
  descricao_fato: string;
  data_ocorrencia: string;
  notas_resolucao: string | null;
  registrado_por: string;
  resolvido_por: string | null;
  resolvido_em: string | null;
  dados_extras: Record<string, unknown> | null;
  // V1 extensions
  valor_estimado: number;
  notificado_em: string | null;
  notificado_por: string | null;
  prazo_resposta: string | null;
  created_at: string;
  updated_at: string;
  // joined
  penalidade_codigo?: string;
  penalidade_tipo?: string;
  penalidade_descricao?: string;
}

export const TIPO_PENALIDADE_OPTIONS = [
  { value: 'atraso', label: 'Atraso' },
  { value: 'nao_conformidade', label: 'Não Conformidade' },
  { value: 'desempenho', label: 'Desempenho' },
  { value: 'seguranca', label: 'Segurança' },
] as const;

export const STATUS_OCORRENCIA_OPTIONS = [
  { value: 'identificada', label: 'Identificada' },
  { value: 'notificada', label: 'Notificada' },
  { value: 'aplicada', label: 'Aplicada' },
  { value: 'contestada', label: 'Contestada' },
  { value: 'cancelada', label: 'Cancelada' },
] as const;

export function useEmpPenalidades(contratoId: string) {
  const { data: penalidades = [], isLoading } = useQuery({
    queryKey: ['emp-penalidades', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_penalidades' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .order('codigo');
      if (error) throw error;
      return (data || []) as unknown as EmpPenalidade[];
    },
    enabled: !!contratoId,
  });
  return { penalidades, isLoading };
}

export function useEmpPenalidadeOcorrencias(contratoId: string) {
  const { data: ocorrencias = [], isLoading } = useQuery({
    queryKey: ['emp-penalidade-ocorrencias', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_penalidade_ocorrencias' as any)
        .select('*')
        .eq('contrato_id', contratoId)
        .order('data_ocorrencia', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as EmpPenalidadeOcorrencia[];
    },
    enabled: !!contratoId,
  });
  return { ocorrencias, isLoading };
}

export function useEmpPenalidadeMutations(contratoId: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['emp-penalidades', contratoId] });
    qc.invalidateQueries({ queryKey: ['emp-penalidade-ocorrencias', contratoId] });
  };

  const upsertPenalidade = useMutation({
    mutationFn: async (input: {
      id?: string;
      codigo: string;
      tipo: string;
      descricao: string;
      clausula_contratual?: string;
      impacto_descricao?: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const payload = {
        contrato_id: contratoId,
        codigo: input.codigo,
        tipo: input.tipo,
        descricao: input.descricao,
        clausula_contratual: input.clausula_contratual || null,
        impacto_descricao: input.impacto_descricao || null,
        updated_at: new Date().toISOString(),
      };

      if (input.id) {
        const { error } = await supabase
          .from('emp_penalidades' as any)
          .update(payload)
          .eq('id', input.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('emp_penalidades' as any)
          .insert({ ...payload, created_by: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Penalidade salva' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao salvar penalidade', description: e.message, variant: 'destructive' }),
  });

  const togglePenalidade = useMutation({
    mutationFn: async ({ id, ativo }: { id: string; ativo: boolean }) => {
      const { error } = await supabase
        .from('emp_penalidades' as any)
        .update({ ativo, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Status da penalidade atualizado' });
    },
    onError: (e: Error) => toast({ title: 'Erro', description: e.message, variant: 'destructive' }),
  });

  const createOcorrencia = useMutation({
    mutationFn: async (input: {
      penalidade_id: string;
      atividade_id?: string;
      desvio_id?: string;
      descricao_fato: string;
      data_ocorrencia?: string;
      valor_estimado?: number;
      prazo_resposta?: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_penalidade_ocorrencias' as any)
        .insert({
          penalidade_id: input.penalidade_id,
          contrato_id: contratoId,
          atividade_id: input.atividade_id || null,
          desvio_id: input.desvio_id || null,
          descricao_fato: input.descricao_fato,
          data_ocorrencia: input.data_ocorrencia || new Date().toISOString().slice(0, 10),
          registrado_por: user.id,
          valor_estimado: input.valor_estimado || 0,
          prazo_resposta: input.prazo_resposta || null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Ocorrência registrada' });
    },
    onError: (e: Error) => toast({ title: 'Erro ao registrar ocorrência', description: e.message, variant: 'destructive' }),
  });


  const updateOcorrenciaStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      notas,
      valor_estimado,
      prazo_resposta,
    }: {
      id: string;
      status: string;
      notas?: string;
      valor_estimado?: number;
      prazo_resposta?: string;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const update: any = { status, updated_at: new Date().toISOString() };
      if (notas) update.notas_resolucao = notas;
      if (valor_estimado !== undefined) update.valor_estimado = valor_estimado;
      if (prazo_resposta) update.prazo_resposta = prazo_resposta;
      if (status === 'cancelada' || status === 'aplicada') {
        update.resolvido_por = user.id;
        update.resolvido_em = new Date().toISOString();
      }
      if (status === 'notificada') {
        update.notificado_por = user.id;
        update.notificado_em = new Date().toISOString();
      }
      const { error } = await supabase
        .from('emp_penalidade_ocorrencias' as any)
        .update(update)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Ocorrência atualizada' });
    },
    onError: (e: Error) => toast({ title: 'Erro', description: e.message, variant: 'destructive' }),
  });

  const createOcorrenciaFromQualityGate = useMutation({
    mutationFn: async (input: {
      penalidade_id: string;
      execucao_id: string;
      descricao_fato: string;
      valor_estimado?: number;
    }) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase
        .from('emp_penalidade_ocorrencias' as any)
        .insert({
          penalidade_id: input.penalidade_id,
          contrato_id: contratoId,
          descricao_fato: input.descricao_fato,
          data_ocorrencia: new Date().toISOString().slice(0, 10),
          registrado_por: user.id,
          valor_estimado: input.valor_estimado || 0,
          dados_extras: { origem: 'quality_gate', execucao_id: input.execucao_id },
        });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Ocorrência vinculada ao Quality Gate' });
    },
    onError: (e: Error) => toast({ title: 'Erro', description: e.message, variant: 'destructive' }),
  });

  return { upsertPenalidade, togglePenalidade, createOcorrencia, updateOcorrenciaStatus, createOcorrenciaFromQualityGate };
}
